import type { Block, CollectionBlock, Narration, NarrationClip, PublishedPayload, Writing } from './types'
import { backendConfig, burrowAppUrl } from './env'
import { narrationFromClips } from './narration'
import { upgradePins } from './blocks'

// URLs inside a writing come from its author, and the blocks jsonb can hold
// anything the owner PATCHes, whatever the composer's forms allow. So before a
// reader renders a writing, links are held to http(s)/mailto and media to
// places Writings itself stores it (the configured project's public storage,
// this app's own assets, inline data images/audio, or demo clocks). Anything
// else is dropped, not rendered.

const LINK_RE = /^(https?:|mailto:)/i
const DATA_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp);/i
const DATA_AUDIO_RE = /^data:audio\/[a-z0-9.+-]+[;,]/i
const STORAGE_PATH = '/storage/v1/object/public/'

/** A link target safe to put in an href, or undefined. */
export function safeHref(url: string | null | undefined): string | undefined {
  const u = (url ?? '').trim()
  return LINK_RE.test(u) ? u : undefined
}

export interface MediaPolicy {
  /** Origin of the page (same-origin assets). */
  appOrigin: string | null
  /** Origin of the Supabase project whose public storage is trusted, if any. */
  storageOrigin: string | null
}

function currentPolicy(): MediaPolicy {
  let storageOrigin: string | null = null
  if (backendConfig.mode === 'supabase') {
    try {
      storageOrigin = new URL(backendConfig.url).origin
    } catch {
      storageOrigin = null
    }
  }
  const appOrigin = typeof window !== 'undefined' && window.location.origin !== 'null' ? window.location.origin : null
  return { appOrigin, storageOrigin }
}

/** An image/audio source Writings trusts, or null. */
export function safeMediaSrc(
  src: string | null | undefined,
  kind: 'image' | 'audio',
  policy: MediaPolicy = currentPolicy(),
): string | null {
  const s = (src ?? '').trim()
  if (!s) return null
  if (kind === 'audio' && s.startsWith('demo:')) return s
  if (kind === 'image' && DATA_IMAGE_RE.test(s)) return s
  if (kind === 'audio' && DATA_AUDIO_RE.test(s)) return s
  // The app's own bundled assets ("/assets/…", or "/src/assets/…" in dev).
  if (s.startsWith('/') && !s.startsWith('//')) return s
  let url: URL
  try {
    url = new URL(s)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  if (policy.storageOrigin && url.origin === policy.storageOrigin && url.pathname.startsWith(STORAGE_PATH)) return s
  if (policy.appOrigin && url.origin === policy.appOrigin) return s
  return null
}

const SLUG_PATH_RE = /^[A-Za-z0-9_-]{1,120}$/
const COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]{3,20}|(rgb|hsl)a?\([0-9.,%\s/]+\))$/i

/** A collection's public page on the main Burrow app, from its slug, or null for one that isn't a plain slug. */
export function collectionPageUrl(slug: string | null | undefined): string | null {
  return slug && SLUG_PATH_RE.test(slug) ? `${burrowAppUrl}/c/${slug}` : null
}

/** A collection card kept to what's safe to render: a plain slug, a plain colour, trusted cover. Null without an id or name. */
export function safeCollection(b: CollectionBlock, policy: MediaPolicy = currentPolicy()): CollectionBlock | null {
  if (typeof b.collectionId !== 'string' || typeof b.name !== 'string' || !b.name.trim()) return null
  const { color, cover, slug, description, ...rest } = b
  const count = Number.isFinite(b.count) && b.count > 0 ? Math.floor(b.count) : 0
  const safeCover = typeof cover === 'string' ? safeMediaSrc(cover, 'image', policy) : null
  return {
    ...rest,
    count,
    ...(typeof color === 'string' && COLOR_RE.test(color.trim()) ? { color: color.trim() } : {}),
    ...(safeCover ? { cover: safeCover } : {}),
    ...(typeof description === 'string' && description.trim() ? { description } : {}),
    ...(typeof slug === 'string' && SLUG_PATH_RE.test(slug) ? { slug } : {}),
  }
}

/** Blocks with unsafe links and media removed (see the note at the top). */
export function safeBlocks(blocks: Block[], policy: MediaPolicy = currentPolicy()): Block[] {
  const out: Block[] = []
  // Old pins (saved before they became margin notes) are upgraded first; see upgradePins().
  for (const b of upgradePins(blocks)) {
    switch (b.type) {
      case 'linkcard': {
        const url = safeHref(b.url)
        if (url) out.push({ ...b, url })
        break
      }
      case 'collection': {
        const c = safeCollection(b, policy)
        if (c) out.push(c)
        break
      }
      case 'image': {
        const src = safeMediaSrc(b.src, 'image', policy)
        if (src) out.push({ ...b, src })
        break
      }
      case 'scene': {
        const src = b.mode === 'image' ? safeMediaSrc(b.src, 'image', policy) : null
        out.push(src ? { ...b, src } : { ...b, mode: 'solid', src: undefined })
        break
      }
      case 'voice': {
        const src = safeMediaSrc(b.src, 'audio', policy)
        if (src) out.push({ ...b, src })
        break
      }
      case 'thought':
      case 'margin':
      case 'unfold': {
        if (!b.source?.url) {
          out.push(b)
          break
        }
        const url = safeHref(b.source.url)
        out.push({ ...b, source: { ...b.source, url } })
        break
      }
      default:
        out.push(b)
    }
  }
  return out
}

/**
 * A narration with only trusted audio: the single file's src, and each
 * piece's src (unsafe pieces are dropped, and the timing re-derived from the
 * pieces that are left). Null when nothing playable remains.
 */
export function safeNarration(n: Narration | null | undefined, policy: MediaPolicy = currentPolicy()): Narration | null {
  if (!n || typeof n !== 'object') return null
  const src = safeMediaSrc(n.src, 'audio', policy)
  const raw = Array.isArray(n.clips) ? n.clips : []
  const clips: NarrationClip[] = []
  for (const c of raw) {
    const clipSrc = c && typeof c === 'object' && typeof c.blockId === 'string' ? safeMediaSrc(c.src, 'audio', policy) : null
    if (clipSrc) clips.push({ ...c, src: clipSrc })
  }
  if (clips.length > 0) {
    if (clips.length === raw.length) return { ...n, src: src ?? '', clips }
    return { ...n, ...narrationFromClips(clips), recorded_at: n.recorded_at }
  }
  if (!src) return null
  // A single file: any pieces that were there were unsafe, so none are kept.
  const { clips: _dropped, ...single } = n
  return { ...single, src }
}

/** A published payload made safe to render. */
export function safePublished(p: PublishedPayload, policy: MediaPolicy = currentPolicy()): PublishedPayload {
  const w = p.writing
  return {
    ...p,
    writing: {
      ...w,
      cover_url: safeMediaSrc(w.cover_url, 'image', policy),
      ...(w.blocks ? { blocks: safeBlocks(w.blocks, policy) } : {}),
      ...(w.narration !== undefined ? { narration: safeNarration(w.narration, policy) } : {}),
    },
    more: p.more?.map((m) => ({ ...m, cover_url: safeMediaSrc(m.cover_url, 'image', policy) })),
  }
}

/** The owner's own writing, made safe to render in Preview. */
export function safeWriting(w: Writing, policy: MediaPolicy = currentPolicy()): Writing {
  return {
    ...w,
    cover_url: safeMediaSrc(w.cover_url, 'image', policy),
    blocks: safeBlocks(w.blocks, policy),
    narration: safeNarration(w.narration, policy),
  }
}
